/**
 * Driver phone GPS reporter (Feb 2026 — SaaS onboarding).
 *
 * When the tenant's `gps_provider === "phone"`, the driver's tablet becomes
 * the truck's GPS. This hook:
 *   1. Waits for the branding fetch so we know which mode the tenant is in.
 *   2. If "phone", requests Geolocation permission and starts a
 *      `watchPosition` (native — reports fixes as they happen, no polling).
 *   3. Debounces posts to the backend to max ~1 every 30 seconds so we
 *      stay comfortably under the rate limit (4/30s) and don't chew
 *      battery.
 *   4. Filters fixes with accuracy > 200m (cold-start town-centre fixes)
 *      before hitting the network.
 *   5. Sends the CLIENT-SUPPLIED `position.timestamp` — not a server clock
 *      value — so the "last seen" timer reflects the real GPS sample time
 *      even if the tablet queues up posts while offline.
 *
 * Silent no-op on any provider other than "phone". Silent no-op if the
 * browser has no Geolocation API (very old devices). Errors are surfaced
 * via a callback rather than console spam so the driver UI can show a
 * one-liner if permission was denied.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { fetchBranding, useBranding } from "./company";

const MIN_POST_INTERVAL_MS = 30_000; // Stay comfortably under 4/30s rate limit
const ACCURACY_CEILING_M = 200;      // Match backend

export function useDriverPhoneGps({ enabled = true, onStatus } = {}) {
  const branding = useBranding();
  // Also expose the resolved-at-runtime provider so the hook picks up
  // changes made via Settings without a hard refresh. `useBranding` will
  // re-fetch after the 60-second TTL, but for critical mode flips we also
  // force a refresh when the tab regains visibility.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onVis = () => {
      if (document.visibilityState === "visible") {
        fetchBranding({ force: true }).then(() => setTick((t) => t + 1));
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  const lastPostAtRef = useRef(0);
  const watchIdRef = useRef(null);
  // Bag the callback so the effect below doesn't retrigger when the caller
  // passes an inline arrow function (very common React foot-gun).
  const statusCbRef = useRef(onStatus);
  useEffect(() => { statusCbRef.current = onStatus; }, [onStatus]);

  useEffect(() => {
    if (!enabled) return;
    // Wait until branding has arrived — otherwise we might mis-detect the
    // mode on first render and never re-check.
    if (branding == null) return;
    if (branding?.gps_provider !== "phone") {
      statusCbRef.current?.({ state: "disabled", reason: "not-phone-mode" });
      return;
    }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      statusCbRef.current?.({ state: "unsupported" });
      return;
    }

    let cancelled = false;

    const post = async (position) => {
      if (cancelled) return;
      const acc = position.coords.accuracy;
      if (typeof acc === "number" && acc > ACCURACY_CEILING_M) {
        statusCbRef.current?.({ state: "low-accuracy", accuracy: acc });
        return;
      }
      const now = Date.now();
      if (now - lastPostAtRef.current < MIN_POST_INTERVAL_MS) return;
      lastPostAtRef.current = now;
      try {
        await api.post("/positions/report", {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: acc ?? null,
          heading: Number.isFinite(position.coords.heading) ? position.coords.heading : null,
          speed: Number.isFinite(position.coords.speed) ? Math.max(0, position.coords.speed) : null,
          ts: new Date(position.timestamp || Date.now()).toISOString(),
        });
        statusCbRef.current?.({ state: "reporting", at: new Date(now).toISOString() });
      } catch (e) {
        // A 400 with "No truck assigned…" is common if a driver logs in
        // before dispatch assigns them. Surface once, don't spam.
        statusCbRef.current?.({
          state: "error",
          detail: e?.response?.data?.detail || e?.message || "post failed",
        });
      }
    };

    const onError = (err) => {
      statusCbRef.current?.({
        state: "permission-denied-or-error",
        code: err?.code,
        message: err?.message,
      });
    };

    watchIdRef.current = navigator.geolocation.watchPosition(post, onError, {
      enableHighAccuracy: true,
      maximumAge: 15_000,   // Reuse recent fixes if the sensor hasn't updated
      timeout: 20_000,
    });

    return () => {
      cancelled = true;
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [enabled, branding?.gps_provider, branding, tick]);
}
